// =====================================================================
// src/lib/configuracoes.js — o que a tela de Configurações decide, sem DOM
//
// Puro, testado em node. A regra (quem configura, unicidade, reserva que
// trava a desativação) mora no banco (0014); aqui se arruma, agrupa,
// valida o formulário antes de ir à rede e prevê o que "gerar quartos" vai
// criar — para a pessoa ver o número antes de clicar.
// =====================================================================

const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Ordem natural de número de quarto: 101 < 102 < 1001, e 103A depois de 103.
export function chaveDeNumero(n) {
  const s = String(n || '');
  const m = /^(\d+)(.*)$/.exec(s);
  return m ? m[1].padStart(6, '0') + m[2].toLowerCase() : 'z' + s.toLowerCase();
}

// Quartos por andar, cada andar com os quartos em ordem. Andar nulo vai
// para o fim como "Sem andar". Filtro por texto (número) e por inativos.
export function quartosPorAndar(unidades, { texto = '', comInativos = false } = {}) {
  const t = semAcento(texto).trim();
  const lista = (unidades || []).filter(u => (comInativos || u.ativo) && (!t || semAcento(u.numero).includes(t) || semAcento(u.imobiliaria_nome).includes(t)));
  const por = new Map();
  for (const u of lista) {
    const k = u.andar === null || u.andar === undefined ? null : Number(u.andar);
    if (!por.has(k)) por.set(k, []);
    por.get(k).push(u);
  }
  return [...por.entries()]
    .sort((a, b) => (a[0] === null ? 1 : b[0] === null ? -1 : a[0] - b[0]))
    .map(([andar, quartos]) => ({ andar, rotulo: andar === null ? 'Sem andar' : `${andar}º andar`, quartos: quartos.slice().sort((x, y) => chaveDeNumero(x.numero).localeCompare(chaveDeNumero(y.numero))) }));
}

// Totais para o cabeçalho: quartos ativos, andares, capacidade somada.
export function resumoDoHotel(unidades) {
  const ativos = (unidades || []).filter(u => u.ativo);
  const andares = new Set(ativos.map(u => u.andar).filter(a => a !== null && a !== undefined));
  return { quartos: ativos.length, andares: andares.size, leitos: ativos.reduce((s, u) => s + (Number(u.capacidade) || 0), 0), inativos: (unidades || []).length - ativos.length };
}

export function validarQuarto({ numero, andar, capacidade }) {
  const erros = [];
  if (!String(numero || '').trim()) erros.push('Número é obrigatório');
  const a = andar === '' || andar === null || andar === undefined ? null : Number(andar);
  if (a !== null && (!Number.isInteger(a) || a < 0 || a > 200)) erros.push('Andar entre 0 e 200');
  const c = Number(capacidade);
  if (!Number.isInteger(c) || c < 1 || c > 20) erros.push('Capacidade entre 1 e 20');
  return erros;
}

// O que "gerar quartos" vai criar: quantos, quais os primeiros/últimos e
// quantos já existem (e serão pulados). Vazio/erro se a faixa não fecha.
export function previaDeGeracao({ andarDe, andarAte, quartoDe, quartoAte }, existentes = []) {
  const a1 = Number(andarDe), a2 = Number(andarAte), q1 = Number(quartoDe), q2 = Number(quartoAte);
  if (![a1, a2, q1, q2].every(Number.isInteger)) return { erro: 'Preencha as quatro faixas com números inteiros' };
  if (a1 < 0 || a2 > 200 || a1 > a2) return { erro: 'Andares de 0 a 200, início antes do fim' };
  if (q1 < 1 || q2 > 99 || q1 > q2) return { erro: 'Quartos de 1 a 99, início antes do fim' };
  const total = (a2 - a1 + 1) * (q2 - q1 + 1);
  if (total > 2000) return { erro: `${total} quartos de uma vez — confira as faixas (máximo 2000)` };
  const numero = (a, q) => `${a}${String(q).padStart(2, '0')}`;
  const ja = new Set((existentes || []).map(u => String(u.numero)));
  let existem = 0;
  for (let a = a1; a <= a2; a++) for (let q = q1; q <= q2; q++) if (ja.has(numero(a, q))) existem++;
  return { total, novos: total - existem, existem, primeiro: numero(a1, q1), ultimo: numero(a2, q2) };
}

// ── a ficha ──────────────────────────────────────────────────────────
export const GRUPOS_DA_FICHA = Object.freeze({ limpeza: 'Limpeza e arrumação', amenidades: 'Amenidades / higiene' });
export function rotuloDoGrupo(g) { return GRUPOS_DA_FICHA[g] || g; }

// Itens por grupo, na ordem; inativos por último dentro do grupo.
export function fichaPorGrupo(itens) {
  const por = new Map();
  for (const i of (itens || []).slice().sort((a, b) => (a.ordem - b.ordem) || String(a.rotulo).localeCompare(String(b.rotulo)))) {
    if (!por.has(i.grupo)) por.set(i.grupo, []);
    por.get(i.grupo).push(i);
  }
  return [...por.entries()].map(([grupo, lista]) => ({ grupo, rotulo: rotuloDoGrupo(grupo), itens: lista.filter(i => i.ativo).concat(lista.filter(i => !i.ativo)) }));
}

// Chave sugerida a partir do rótulo: "Toalhas de piscina" → toalhas_de_piscina.
export function chaveSugerida(rotulo) {
  return semAcento(rotulo).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 42);
}

export function validarItem({ chave, rotulo, tipo }, { novo = true } = {}) {
  const erros = [];
  if (!String(rotulo || '').trim()) erros.push('Rótulo é obrigatório');
  if (novo && !/^[a-z][a-z0-9_]{1,40}$/.test(String(chave || ''))) erros.push('Chave: letras minúsculas, números e _ (ex.: toalhas_extra)');
  if (!['marcar', 'quantidade'].includes(tipo)) erros.push('Tipo é marcar ou quantidade');
  return erros;
}

// A próxima ordem livre dentro de um grupo (para um item novo ir ao fim).
export function proximaOrdem(itens, grupo) {
  const doGrupo = (itens || []).filter(i => i.grupo === grupo);
  return doGrupo.length ? Math.max(...doGrupo.map(i => Number(i.ordem) || 0)) + 1 : 1;
}

export function validarCliente({ nome }) {
  return String(nome || '').trim() ? [] : ['Nome é obrigatório'];
}

// ── horários e fuso (0019) ───────────────────────────────────────────
// O banco valida o fuso contra pg_timezone_names; aqui só se evita mandar
// vazio. A lista é sugestão (datalist): os fusos do Brasil.
export const FUSOS_BR = Object.freeze(['America/Sao_Paulo', 'America/Manaus', 'America/Cuiaba', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Bahia', 'America/Rio_Branco', 'America/Noronha', 'America/Boa_Vista', 'America/Porto_Velho', 'America/Campo_Grande']);
export function validarHorarios({ hora_checkin, hora_checkout, fuso, virada_dia }) {
  const e = [];
  const h = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!h.test(String(hora_checkin || ''))) e.push('Hora do check-in inválida');
  if (!h.test(String(hora_checkout || ''))) e.push('Hora do check-out inválida');
  if (!h.test(String(virada_dia || ''))) e.push('Hora da virada do dia inválida');
  if (!String(fuso || '').trim()) e.push('Fuso horário é obrigatório');
  return e;
}
// "14:00:00" (como o banco guarda) → "14:00" (como o <input type=time> quer)
export function horaCurtaCfg(h) { const m = /^(\d{2}:\d{2})/.exec(String(h || '')); return m ? m[1] : ''; }

// ── 0103: o contato da imobiliária ───────────────────────────────────
// Para a Recepção ligar quando o cliente chega sem reserva (Hugo, 03/11:
// "o balcão não lança: chama a imobiliária"). A régua de verdade é a de
// `salvar_contato_imobiliaria`; aqui a mesma, para a pessoa ver o erro
// antes de ir à rede. Telefone grava só os dígitos (DDD + número, 10 a 13).
export const soDigitos = s => String(s || '').replace(/\D/g, '');
export function validarContato({ nome, sobrenome, telefone, email }) {
  const erros = [];
  if (!String(nome || '').trim() || !String(sobrenome || '').trim()) erros.push('Informe nome e sobrenome do responsável');
  if (/[^\d()\-+.\s]/.test(String(telefone || ''))) erros.push('Telefone tem só números (parênteses, traço e espaço podem ir)');
  else { const d = soDigitos(telefone); if (d.length < 10 || d.length > 13) erros.push('Telefone com DDD: de 10 a 13 dígitos'); }
  const e = String(email || '').trim();
  if (e && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) erros.push('E-mail inválido');
  return erros;
}
// Algum campo preenchido? O contato é opcional, mas, começado, vai inteiro.
export function contatoComecado({ nome, sobrenome, telefone, email }) {
  return [nome, sobrenome, telefone, email].some(v => String(v || '').trim() !== '');
}
// (61) 99999-0000 / (61) 3333-0000; com código de país, +55 61 99999-0000.
// Outro tamanho volta como veio (só dígitos).
export function formatarTelefone(t) {
  const d = soDigitos(t);
  const local = x => x.length === 11 ? `(${x.slice(0, 2)}) ${x.slice(2, 7)}-${x.slice(7)}` : `(${x.slice(0, 2)}) ${x.slice(2, 6)}-${x.slice(6)}`;
  if (d.length === 10 || d.length === 11) return local(d);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return '+55 ' + local(d.slice(2));
  return d;
}
// O link de discar: sem código de país, é Brasil.
export function linkDoTelefone(t) {
  const d = soDigitos(t);
  if (!d) return null;
  return 'tel:+' + (d.length <= 11 ? '55' + d : d);
}
